// Extrait de DataTab.js (handover bloc F1) — JSX, textes et appels API inchangés.
import { useState, useEffect } from "react";
import api from "@/api";
import { toast } from "sonner";
import { RefreshCw, Plus, Save, BriefcaseBusiness, FileCheck2 } from "lucide-react";
import { QUALITY_LABELS } from "./shared";

export default function DataProjectsView({ filters }) {
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [domains, setDomains] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [feasibility, setFeasibility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: "",
    partnerType: "LABO_PHARMA",
    partnerName: "",
    objective: "",
    indication: filters.cancerType || "",
    molecule: filters.treatmentType || "",
    requiredConsentPurpose: "RESEARCH_PHARMA",
    domains: ["CHECKINS", "PRO", "ORAL_ADHERENCE", "TRANSMISSIONS"],
    deliverables: ["Feasibility", "Dataset CSV", "Dictionnaire", "Rapport insights"],
  });

  const loadProjects = async () => {
    setLoading(true);
    try {
      const [projectRes, templateRes] = await Promise.all([
        api.get("/data/projects"),
        api.get("/data/project-templates"),
      ]);
      setProjects(projectRes.data?.items || []);
      setTemplates(templateRes.data?.templates || []);
      setDomains(templateRes.data?.availableDomains || []);
    } catch (err) {
      toast.error("Erreur chargement Projets Data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const applyTemplate = (tpl) => {
    setForm(prev => ({
      ...prev,
      name: tpl.name,
      partnerType: tpl.partnerType,
      objective: tpl.objective,
      domains: tpl.domains,
      deliverables: tpl.deliverables,
    }));
  };

  const toggleDomain = (domain) => {
    setForm(prev => ({
      ...prev,
      domains: prev.domains.includes(domain)
        ? prev.domains.filter(d => d !== domain)
        : [...prev.domains, domain],
    }));
  };

  const createProject = async () => {
    if (!form.name.trim()) {
      toast.error("Nommez le projet Data");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        scope: {
          clinicId: filters.clinicId || "",
          cancerType: filters.cancerType || form.indication || "",
          treatmentType: filters.treatmentType || "",
          protocol: filters.protocol || "",
          startDate: filters.startDate || "",
          endDate: filters.endDate || "",
        },
        cohortCriteria: [],
      };
      const res = await api.post("/data/projects", payload);
      setSelectedProject(res.data);
      toast.success("Projet Data créé");
      await loadProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur création projet");
    } finally {
      setCreating(false);
    }
  };

  const runFeasibility = async (project) => {
    setSelectedProject(project);
    setFeasibility(null);
    try {
      const res = await api.get(`/data/projects/${project.id}/feasibility`);
      setFeasibility(res.data);
      toast.success("Faisabilité calculée");
      await loadProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur faisabilité");
    }
  };

  const createSnapshot = async (project) => {
    try {
      const res = await api.post(`/data/projects/${project.id}/snapshot`, {});
      toast.success(`Snapshot créé · ${res.data.contentHash?.slice(0, 8)}`);
      await loadProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur snapshot");
    }
  };

  return (
    <div className="space-y-6" data-testid="data-projects-view">
      <div className="dash-card bg-gradient-to-r from-slate-50 to-emerald-50 border-slate-200" data-testid="data-projects-intro-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <BriefcaseBusiness className="w-5 h-5 text-slate-700" />
              Projets Data partenaire
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              Préparez une demande CRO/labo/institution : périmètre, domaines, consentement, faisabilité, snapshot et livrables.
            </p>
          </div>
          <button
            onClick={loadProjects}
            disabled={loading}
            data-testid="data-projects-refresh-button"
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      </div>

      <div className="grid xl:grid-cols-[420px_1fr] gap-5">
        <div className="space-y-4">
          <div className="dash-card" data-testid="data-project-create-card">
            <h4 className="font-semibold text-slate-800 mb-3">Créer un projet</h4>
            <div className="grid gap-3">
              <input data-testid="data-project-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Nom du projet" />
              <div className="grid grid-cols-2 gap-2">
                <select data-testid="data-project-partner-type-select" value={form.partnerType} onChange={e => setForm({ ...form, partnerType: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                  <option value="LABO_PHARMA">Labo pharma</option>
                  <option value="CRO">CRO</option>
                  <option value="INSTITUTION">Institution</option>
                  <option value="RESEARCH">Recherche</option>
                </select>
                <input data-testid="data-project-partner-name-input" value={form.partnerName} onChange={e => setForm({ ...form, partnerName: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Partenaire" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="data-project-indication-input" value={form.indication} onChange={e => setForm({ ...form, indication: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Indication / pathologie" />
                <input data-testid="data-project-molecule-input" value={form.molecule} onChange={e => setForm({ ...form, molecule: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Molécule / protocole" />
              </div>
              <textarea data-testid="data-project-objective-input" value={form.objective} onChange={e => setForm({ ...form, objective: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm min-h-20" placeholder="Objectif / insight attendu" />
              <div data-testid="data-project-domain-picker">
                <p className="text-xs font-medium text-slate-500 mb-2">Domaines à inclure</p>
                <div className="flex flex-wrap gap-2">
                  {domains.map(domain => (
                    <button
                      key={domain}
                      type="button"
                      onClick={() => toggleDomain(domain)}
                      data-testid={`data-project-domain-${domain}`}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${form.domains.includes(domain) ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}
                    >
                      {domain}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={createProject}
                disabled={creating}
                data-testid="data-project-create-button"
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 text-white rounded-lg text-sm font-semibold hover:bg-slate-900 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                {creating ? "Création..." : "Créer le projet"}
              </button>
            </div>
          </div>

          <div className="dash-card" data-testid="data-project-templates-card">
            <h4 className="font-semibold text-slate-800 mb-3">Templates rapides</h4>
            <div className="space-y-2">
              {templates.map(tpl => (
                <button key={tpl.id} onClick={() => applyTemplate(tpl)} data-testid={`data-project-template-${tpl.id}`} className="w-full text-left rounded-lg border border-slate-100 p-3 hover:bg-slate-50 transition-colors">
                  <p className="text-sm font-semibold text-slate-800">{tpl.name}</p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{tpl.objective}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="dash-card" data-testid="data-projects-list-card">
            <h4 className="font-semibold text-slate-800 mb-3">Projets enregistrés</h4>
            {projects.length === 0 ? (
              <div className="text-center py-8 text-slate-400" data-testid="data-projects-empty-state">
                <BriefcaseBusiness className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Aucun projet Data pour le moment</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="data-projects-list">
                {projects.map(project => (
                  <div key={project.id} className="rounded-xl border border-slate-100 p-4" data-testid={`data-project-row-${project.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-800">{project.name}</p>
                        <p className="text-xs text-slate-500 mt-1">{project.partnerType} · {project.partnerName || "Partenaire à préciser"} · v{project.version}</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(project.domains || []).slice(0, 6).map(domain => <span key={domain} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold">{domain}</span>)}
                        </div>
                      </div>
                      <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700">{project.status}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button onClick={() => runFeasibility(project)} data-testid={`data-project-feasibility-${project.id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
                        <FileCheck2 className="w-3.5 h-3.5" /> Faisabilité
                      </button>
                      <button onClick={() => createSnapshot(project)} data-testid={`data-project-snapshot-${project.id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                        <Save className="w-3.5 h-3.5" /> Snapshot
                      </button>
                    </div>
                    {project.lastSnapshot && (
                      <p className="mt-2 text-[11px] text-slate-400" data-testid={`data-project-last-snapshot-${project.id}`}>
                        Snapshot : {project.lastSnapshot.consentedPatients}/{project.lastSnapshot.eligiblePatients} consentis · hash {project.lastSnapshot.contentHash?.slice(0, 8)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {feasibility && (
            <div className="dash-card border-emerald-200 bg-emerald-50/40" data-testid="data-project-feasibility-card">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-800">Faisabilité · {feasibility.projectName}</h4>
                <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-700" data-testid="data-project-quality-score">Score {feasibility.qualityScore}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <ProjectMetric label="Éligibles" value={feasibility.population?.eligiblePatients || 0} testid="project-eligible-patients" />
                <ProjectMetric label="Consentis" value={feasibility.population?.consentedPatients || 0} testid="project-consented-patients" />
                <ProjectMetric label="Check-ins" value={feasibility.dataCoverage?.checkins || 0} testid="project-checkins" />
                <ProjectMetric label="Transmissions" value={feasibility.dataCoverage?.structuredTransmissionEvents || 0} testid="project-transmissions" />
              </div>
              <div className="mt-4 grid md:grid-cols-2 gap-3">
                {Object.entries(feasibility.completeness || {}).map(([key, value]) => (
                  <div key={key} className="rounded-lg bg-white p-3" data-testid={`project-completeness-${key}`}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-500">{QUALITY_LABELS[key] || key}</span><span className="font-bold text-slate-800">{value}%</span></div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${Math.min(value || 0, 100)}%` }} /></div>
                  </div>
                ))}
              </div>
              {(feasibility.warnings || []).length > 0 && (
                <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 p-3" data-testid="project-feasibility-warnings">
                  {(feasibility.warnings || []).map((warning, idx) => <p key={idx} className="text-xs font-semibold text-amber-800">• {warning}</p>)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function ProjectMetric({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-white p-3 border border-slate-100" data-testid={testid}>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}
